<template>
  <div
    class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4"
  >
    <div
      class="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row"
    >
      <!-- Left Side - Visual Content -->
      <div
        class="hidden md:block md:w-1/2 bg-gradient-to-br from-indigo-600 p-8 flex flex-col justify-center"
        style="background: #4337ca"
      >
        <div class="text-white max-w-xs mx-auto">
          <h1 class="text-3xl font-bold mb-4">{{ t("sign.welcomeBack") }}</h1>
          <p class="text-blue-100 mb-8">
            {{
              currentForm === "login"
                ? t("sign.loginToAccess")
                : currentForm === "register"
                ? t("sign.joinOurCommunity")
                : t("sign.weWillHelpYou")
            }}
          </p>

          <img
            src="@/assets/login1.png"
            alt="Authentication illustration"
            class="w-full h-auto"
          />
        </div>
      </div>

      <!-- Right Side - Form Content -->
      <div class="w-full md:w-1/2 p-8 md:p-10">
        <div class="flex justify-center mb-6">
          <div
            class="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-8 w-8 text-teal-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
              />
            </svg>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-center text-gray-800 mb-1">
          {{
            currentForm === "login"
              ? t("sign.siginIn")
              : currentForm === "register"
              ? t("sign.createAccount")
              : t("sign.restetPassword")
          }}
        </h2>
        <p class="text-center text-gray-500 mb-8">
          {{
            currentForm === "login"
              ? t("sign.EnterYourCredentials")
              : currentForm === "register"
              ? t("sign.getStarted")
              : t("sign.enterYourEmail")
          }}
        </p>

        <!-- Login Form -->
        <form
          v-if="currentForm === 'login'"
          @submit.prevent="login"
          class="space-y-5"
        >
          <!-- Success message after registration -->
          <div
            v-if="showSuccessMessage"
            class="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800"
          >
            <div class="flex items-center">
              <svg
                class="h-5 w-5 text-green-500 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <p class="font-medium">Registration successful! Please log in.</p>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{
              t("sign.email")
            }}</label>
            <div class="relative">
              <input
                type="email"
                v-model="form.email"
                :placeholder="t('sign.email')"
                :class="[
                  'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                  isRTL
                    ? 'text-right placeholder:text-right'
                    : 'text-left placeholder:text-left',
                ]"
              />
              <div
                :class="[
                  'absolute inset-y-0 flex items-center text-gray-400 pointer-events-none',
                  isRTL ? 'left-3' : 'right-3',
                ]"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
                  />
                  <path
                    d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{
              t("sign.password")
            }}</label>
            <div class="relative">
              <input
                type="password"
                v-model="form.password"
                :placeholder="t('sign.password')"
                :class="[
                  'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                  isRTL
                    ? 'text-right placeholder:text-right'
                    : 'text-left placeholder:text-left',
                ]"
              />
              <div
                :class="[
                  'absolute inset-y-0 flex items-center text-gray-400 pointer-events-none',
                  isRTL ? 'left-3' : 'right-3',
                ]"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <label class="flex items-center gap-1">
              <input
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span class="ml-2 text-sm text-gray-600">{{
                t("sign.rememberMe")
              }}</span>
            </label>
            <button
              type="button"
              @click="currentForm = 'forgot'"
              class="text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              {{ t("sign.forgotPassword") }}
            </button>
          </div>

          <button
            type="submit"
            class="w-full bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition"
          >
            {{ t("sign.siginIn") }}
          </button>

          <div class="text-center text-sm text-gray-500">
            {{ t("sign.dontHaveAccount") }}
            <button
              type="button"
              @click="currentForm = 'register'"
              class="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {{ t("sign.signUp") }}
            </button>
          </div>
        </form>

        <!-- Register Form -->
        <form
          v-else-if="currentForm === 'register'"
          @submit.prevent="register"
          class="space-y-5"
        >
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{
              t("sign.fullName")
            }}</label>
            <div class="relative">
              <input
                type="text"
                v-model="form.name"
                :placeholder="t('sign.fullName')"
                required
                :class="[
                  'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                  isRTL
                    ? 'text-right placeholder:text-right'
                    : 'text-left placeholder:text-left',
                ]"
              />
              <div
                :class="[
                  'absolute inset-y-0 flex items-center text-gray-400 pointer-events-none',
                  isRTL ? 'left-3' : 'right-3',
                ]"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Email</label
            >
            <div class="relative">
              <input
                type="email"
                v-model="form.email"
                :placeholder="t('sign.email')"
                :class="[
                  'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                  isRTL
                    ? 'text-right placeholder:text-right'
                    : 'text-left placeholder:text-left',
                ]"
              />
              <div
                :class="[
                  'absolute inset-y-0 flex items-center text-gray-400 pointer-events-none',
                  isRTL ? 'left-3' : 'right-3',
                ]"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
                  />
                  <path
                    d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Password</label
            >
            <div class="relative">
              <input
                type="password"
                v-model="form.password"
                :placeholder="t('sign.password')"
                :class="[
                  'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                  isRTL
                    ? 'text-right placeholder:text-right'
                    : 'text-left placeholder:text-left',
                ]"
              />
              <div
                :class="[
                  'absolute inset-y-0 flex items-center text-gray-400 pointer-events-none',
                  isRTL ? 'left-3' : 'right-3',
                ]"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div class="flex items-start">
            <div class="flex items-center h-5">
              <input
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                required
              />
            </div>
            <div class="ml-3 text-sm">
              <label class="font-medium text-gray-700">{{
                t("sign.iAgreeToThe")
              }}</label>
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                class="text-indigo-600 hover:text-indigo-500"
              >
                {{ t("sign.terms&conditions") }}
              </a>
            </div>
          </div>

          <button
            type="submit"
            class="w-full bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition"
          >
            {{ t("sign.signUp") }}
          </button>

          <div class="text-center text-sm text-gray-500">
            {{ t("sign.alreadyHaveAccount") }}
            <button
              type="button"
              @click="currentForm = 'login'"
              class="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {{ t("sign.siginIn") }}
            </button>
          </div>
        </form>

        <!-- Forgot Password Form -->
        <form
          v-else-if="currentForm === 'forgot'"
          @submit.prevent="resetPassword"
          class="space-y-5"
        >
          <div v-if="!password_reset_form.email_sent">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t("sign.email") }}</label
            >
            <div class="relative">
              <input
                type="email"
                v-model="password_reset_form.email"
                :placeholder="t('sign.email')"
                :class="[
                  'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                  isRTL
                    ? 'text-right placeholder:text-right'
                    : 'text-left placeholder:text-left',
                ]"
              />
              <div
                :class="[
                  'absolute inset-y-0 flex items-center text-gray-400 pointer-events-none',
                  isRTL ? 'left-3' : 'right-3',
                ]"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
                  />
                  <path
                    d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div
            v-else-if="
              password_reset_form.email_sent && !password_reset_form.otp_sent
            "
          >
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t("sign.otp") }}</label
            >
            <div class="relative">
              <input
                type="text"
                v-model="password_reset_form.otp"
                :placeholder="t('sign.otp')"
                :class="[
                  'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                  isRTL
                    ? 'text-right placeholder:text-right'
                    : 'text-left placeholder:text-left',
                ]"
              />
            </div>
          </div>
          <div class="space-y-2" v-else>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t("sign.new_password") }}</label
              >
              <div class="relative">
                <input
                  type="password"
                  v-model="password_reset_form.new_password"
                  :placeholder="t('sign.new_password')"
                  :class="[
                    'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                    isRTL
                      ? 'text-right placeholder:text-right'
                      : 'text-left placeholder:text-left',
                  ]"
                />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t("sign.new_password_confirm") }}</label
              >
              <div class="relative">
                <input
                  type="password"
                  v-model="password_reset_form.new_password_confirm"
                  :placeholder="t('sign.new_password_confirm')"
                  :class="[
                    'w-full py-3 pr-10 pl-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition',
                    isRTL
                      ? 'text-right placeholder:text-right'
                      : 'text-left placeholder:text-left',
                  ]"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            class="w-full bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition"
          >
            {{ t("sign.sendRestetLink") }}
          </button>

          <div class="text-center text-sm text-gray-500">
            {{ t("sign.rememberedYourPassword") }}
            <button
              type="button"
              @click="currentForm = 'login'"
              class="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {{ t("sign.siginIn") }}
            </button>
          </div>
        </form>

        <!-- Social Login Divider -->
        <div class="mt-8">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">{{
                t("sign.or")
              }}</span>
            </div>
          </div>

          <div class="mt-6 w-full flex justify-center items-center">
            <button
              @click="handleGoogleLogin"
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <svg
                class="w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span>{{ t("sign.signInWithGoogle") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"; // adjust path if needed
import Swal from "sweetalert2";
import useAuth from "@/composables/useAuth";
import { useI18n } from "vue-i18n";
import axios from "axios";

export default {
  name: "AuthPage",
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      currentForm: "login", // 'login', 'register', or 'forgot'
      form: {
        name: "",
        email: "",
        password: "",
      },
      password_reset_form: {
        email: "",
        email_sent: false,
        otp: "",
        otp_sent: false,
        new_password: "",
        new_password_confirm: "",
      },
    };
  },
  methods: {
    handleGoogleLogin() {
      // const googleRedirectUrl = import.meta.env.VITE_API_BASE_URL + '/auth/google/redirect';
      window.location.href =
        "https://admin.from-malk.com/api/auth/google/redirect";
    },
    async login() {
      if (!this.form.email || !this.form.password) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please enter both email and password",
        });
        return;
      }
      try {
        const response = await api.post("/login", {
          email: this.form.email,
          password: this.form.password,
        });

        const token = response.data.access_token;
        const user = response.data.user;
        const userId = response.data.id;
        if (token) {
          // Save token to localStorage
          localStorage.setItem("access_token", token);
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("userId", userId);
          // Update global auth state
          const { login } = useAuth();
          login(token);

          const redirectPath = this.$route.query.redirect || "/";
          this.$router.push(redirectPath);
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text:
            error.response?.data?.message ||
            "Please check your email and password.",
        });
      }
    },
    async register() {
      if (!this.form.name || !this.form.email || !this.form.password) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please fill in all fields",
        });
        return;
      }

      try {
        const response = await api.post("/register", this.form);

        const token = response.data.access_token;
        const user = response.data.user;
        const userId = user?.id;

        if (token && user) {
          localStorage.setItem("access_token", token);
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("userId", userId);

          const { login } = useAuth();
          login(token);

          const redirectPath = this.$route.query.redirect || "/";
          this.$router.push(redirectPath);
        }
      } catch (error) {
        console.error("🔥 FULL Axios error object:", error);

        let errorMessage = "Unknown error occurred.";
        const lang = localStorage.getItem("lang_code") || "ar";

        if (error.response && error.response.data) {
          console.log("✅ error.response.data:", error.response.data);

          const data = error.response.data;

          // Check if custom keys exist
          if (lang === "ar" && data.ar_error) {
            errorMessage = data.ar_error;
          } else if (lang === "en" && data.en_error) {
            errorMessage = data.en_error;
          } else {
            // fallback to Laravel default validation errors
            const firstKey = Object.keys(data)[0];
            errorMessage = Array.isArray(data[firstKey])
              ? data[firstKey][0]
              : data[firstKey];
          }
        } else {
          console.warn("⚠️ No response data found in error object");
        }

        Swal.fire({
          icon: "error",
          title: lang === "ar" ? "خطأ" : "Error",
          text: errorMessage,
        });
      }
    },
    async resetPassword() {
      const lang = localStorage.getItem("lang_code") || "ar";

      if (!this.password_reset_form.email_sent) {
        try {
          await axios.post("https://admin.from-malk.com/api/forgot-password", {
            email: this.password_reset_form.email,
          });
          this.password_reset_form.email_sent = true;

          Swal.fire({
            icon: "success",
            title:
              lang === "ar"
                ? "تم إرسال تعليمات إعادة التعيين"
                : "Reset instructions sent",
            timerProgressBar: true,
            timer: 2000,
          });
        } catch {
          Swal.fire({
            icon: "error",
            title:
              lang === "ar"
                ? "تم إرسال تعليمات إعادة التعيين"
                : "Reset instructions sent",
            timerProgressBar: true,
            timer: 2000,
          });
        }
      } else if (
        this.password_reset_form.email_sent &&
        !this.password_reset_form.otp_sent
      ) {
        try {
          await axios.post("https://admin.from-malk.com/api/verify-code", {
            email: this.password_reset_form.email,
            otp: this.password_reset_form.otp,
          });
          this.password_reset_form.otp_sent = true;

          Swal.fire({
            icon: "success",
            title:
              lang === "ar"
                ? "تم التحقق من رمز OTP"
                : "OTP verified successfully",
            timerProgressBar: true,
            timer: 2000,
          });
        } catch {
          Swal.fire({
            icon: "error",
            title: lang === "ar" ? "رمز التحقق خاطئ" : "Invalid OTP",
            timerProgressBar: true,
            timer: 2000,
          });
        }
      } else {
        if (
          this.password_reset_form.new_password !=
          this.password_reset_form.new_password_confirm
        ) {
          Swal.fire({
            icon: "error",
            title:
              lang === "ar"
                ? "كلمة المرور ليست متطابقة"
                : "Passwords do not match",
            timerProgressBar: true,
            timer: 2000,
          });
          return;
        }

        try {
          await axios.post("https://admin.from-malk.com/api/reset-password", {
            email: this.password_reset_form.email,
            otp: this.password_reset_form.otp,
            password: this.password_reset_form.new_password,
          });

          Swal.fire({
            icon: "success",
            title:
              lang === "ar"
                ? "تم تغيير كلمة المرور بنجاح"
                : "Your password has been changed successfully",
            timerProgressBar: true,
            timer: 2000,
            didClose: () => {
              this.currentForm = "login";
            },
          });
        } catch {
          alert(
            lang === "ar"
              ? "حصلت مشكلة أثناء عملية تغيير كلمة المرور"
              : "Unexpected Error"
          );
        }
      }
    },
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const userEncoded = urlParams.get("user");

    if (token && userEncoded) {
      try {
        const user = JSON.parse(atob(userEncoded));

        localStorage.setItem("access_token", token);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("userId", user.id);

        const { login } = useAuth();
        login(token);

        // Clean the URL so it doesn't stay with ?token=...
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        // Redirect to home or dashboard
        this.$router.push("/");
      } catch (error) {
        console.error("Failed to parse Google login response:", error);
      }
    }
  },
};
</script>

<style scoped>
/* Custom transitions for smoother interactions */
button,
input,
a {
  transition: all 0.2s ease;
}

/* Focus styles */
input:focus,
button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.3);
}
</style>
